import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormGroup, FormInput, Heading, Input, SectionPage } from '@snsw/react-component-library';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { useSelector } from 'react-redux';
import styles from '../PlantDesignDetails.module.scss';
import { capitalizeFirstLetter } from 'shared/utils/Helpers';
import { findPlantRefByPlantTypeCode } from 'plantDesign/constants/PlantDesignRefData';

export const CommonDesignDetails = React.forwardRef((props, ref) => {
  const savedPlantDesign = useSelector((state) => state.plantDesign);

  const [manufacturer, setManufacturer] = useState(savedPlantDesign.manufacturer);
  const [modelNameOrNumber, setModelNameOrNumber] = useState(savedPlantDesign.modelNameOrNumber);
  const [yearOfDesign, setYearOfDesign] = useState(savedPlantDesign.yearOfDesign);

  const [plantRef, setPlantRef] = useState(undefined);
  const [yearInputLabel, setYearInputLabel] = useState('');

  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const PAGE_CONSTANTS = PlantDesignDetailsPage.common;
  const REG_TYPES = PAGE_CONSTANTS.registrationTypes;

  useEffect(() => {
    props.onErrorChange(errorSummary);
  }, [errorSummary]);

  useEffect(() => {
    setPlantRef(findPlantRefByPlantTypeCode(props.plantType));
  }, [props.plantType]);

  useEffect(() => {
    if (props.registrationType === REG_TYPES.new.value) {
      setYearInputLabel(REG_TYPES.new.yearLabel);
    } else if (props.registrationType === REG_TYPES.alteration.value) {
      setYearInputLabel(REG_TYPES.alteration.yearLabel);
    }
  }, [plantRef, props.registrationType]);

  const validators = [
    {
      fieldName: 'modelNameOrNumber',
      fieldValue: modelNameOrNumber,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isValid = validateExt(validators, setErrors, setErrorSummary);
        return isValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      manufacturer,
      modelNameOrNumber,
      yearOfDesign,
    };

    props.onDetailsChange(details);

    if (props.showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [manufacturer, modelNameOrNumber, yearOfDesign]);

  return (
    <>
      <SectionPage>
        <Heading level={2} className={styles.plantTypeTitle}>
          {`${capitalizeFirstLetter(plantRef?.name)} details`}
        </Heading>

        <span className={styles.plantTypeSubTitle}>
          The plant design details you provide must be consistent with the drawings
        </span>

        <FormGroup
          id="manufacturer"
          label={PAGE_CONSTANTS.manufacturer.label}
          errorMessage={errors?.manufacturer?.errorMessage}
          hasError={props.showErrors && errors?.manufacturer?.invalid}
          margin={{ top: 'lg' }}
        >
          <Input value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} inputWidth="xl" />
        </FormGroup>

        <FormGroup
          id="modelNameOrNumber"
          label={PAGE_CONSTANTS.modelNameOrNumber.label}
          helpMessage={PAGE_CONSTANTS.modelNameOrNumber.helpMessage}
          isRequired
          errorMessage={errors?.modelNameOrNumber?.errorMessage}
          hasError={props.showErrors && errors?.modelNameOrNumber?.invalid}
          margin={{ top: 'lg' }}
        >
          <Input value={modelNameOrNumber} onChange={(e) => setModelNameOrNumber(e.target.value)} />
        </FormGroup>

        <FormInput
          label={yearInputLabel}
          id="yearOfDesign"
          errorMessage={errors?.yearOfDesign?.errorMessage}
          hasError={props.showErrors && errors?.yearOfDesign?.invalid}
          value={yearOfDesign}
          onChange={(e, { value }) => setYearOfDesign(value)}
          inputWidth="xs"
          placeholder="YYYY"
          mask={[/\d/, /\d/, /\d/, /\d/]}
          margin={{ top: 'lg' }}
        />
      </SectionPage>
    </>
  );
});

CommonDesignDetails.displayName = 'PlantDesignDetailsCommon';

CommonDesignDetails.propTypes = {
  plantType: PropTypes.string,
  registrationType: PropTypes.string,
  onChange: PropTypes.func,
  onErrorChange: PropTypes.func,
  showErrors: PropTypes.bool,
};
